import _ from "lodash";
import { Context } from "csi/def/Context";
import { C8AttributeValue, C8Node, ICache, cnl } from "csi/typings";
import xml from "csi/xml";
import csi from "csi/main";
import types from "csi/types";
import resolver from "csi/resolver";
import reflection from "csi/reflection";
import ClientRollupScope from "./ClientRollupScope";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { IEvaluateParam } from "./ServerRollupScope";

/**
 * Evaluate expression using the context. 
 * TS does not allow the use of the 'with' in strict mode so added this function outside the class
 */
function evalWithContext(ctx: ClientRollupScope, expr: string): any {
	return eval(`with (ctx) ${expr}`);
}

/**
 * contains the failed expression with expkey and the status
 */
const _failed: Record<string, boolean> = {};
const PSEUDO_ATTRIBUTES_MAP = {
	"Node CNL": "$CNL",
	"Node Type": "$Type",
	"Node Name": "$Name",
	"Node Crew": "Node Crew",
	"Node Flags": "$FG",
	"Node Creator CNL": "$CR",
	"Node Created Time": "$CT",
	"Node Publisher CNL": "$PB",
	"Node Published Time": "$PT",
	"Node Version": "$VR",
};

class Processor {
	/**
	 * cache for looking up nodes 
	 */
	private cache: ICache = null;

	/**
	 * context to be used in expression (optional)
	 */
	private context: Context = null;

	/**
	 * map of attribute expression dependencies
	 */
	private _dependencyIds: C8AttributeValue = null;

	/**
	 * map of evaluated attributes from URL to attribute id
	 */
	private _evalIds: C8AttributeValue = null;

	/**
	 * current node in context
	 */
	private nodeCurrent: C8Node = null;

	/**
	 * current time in milliseconds
	 */
	timeCurrent = 0;


	constructor(node?: C8Node, cache?: ICache, ctx?: Context) {
		this.reset(node, cache, ctx);
		this.setNowTime();
	}

	/**
	 * Reset members
	 */
	reset(node?: C8Node, cache?: ICache, ctx?: Context): void {
		this.cache = cache;
		this.context = ctx;
		this.nodeCurrent = node;
		this._dependencyIds = null;
		this._evalIds = {};
	}

	/**
	 * return attribute ids used by expressions
	 */
	getDependencies(): C8AttributeValue {
		return this._dependencyIds;
	}

	/**
	 * Return URL of result node in current context; if in a property view then there are no query results and
	 * thus just need to return the node at the root of the view
	 */
	getResultNodeURL(): cnl {
		const { queryResult, view } = this.context;
		return queryResult?.url || view.urlRoot;
	}

	/**
	 * Store current time for nowTime function
	 */
	setNowTime(): void {
		this.timeCurrent = types.getDateTime();
	}

	/**
	 * Output exception to console
	 */
	toConsole(ex: any, expr: string): void {
		var s = ex.message || "";

		if (s == csi.EMPTY_URL) {
			return;
		} else if (xml.isURL(s)) {
			s = "missing url \"" + s + "\"";
		}

		console.warn("Rollup: " + s + " - " + expr);
	}

	/**
	 * evaluate expression on current node
	 */
	evaluate(expr: string, value: string, node?: C8Node, params?: IEvaluateParam): any {
		if (!expr || (typeof expr !== "string")) {
			return expr;
		}

		if (expr.trim() && !isNaN(Number(expr))) {
			return Number(expr);
		}

		if (!node && (value == null) && (expr.indexOf("(") < 0)) {
			return expr;
		}

		const f = new ClientRollupScope(this, node || this.nodeCurrent, this.cache || resolver.m_cache, value, params);
		const v = evalWithContext(f, expr);
		return v;
	}

	/**
	 * return value for attribute on node
	 */
	evaluateAttribute(idAttr: string, nodeSource?: C8Node, bDependency?: boolean, params?: IEvaluateParam): any {
		const node = nodeSource || this.nodeCurrent;
		if (!node) {
			return null;
		}

		// first determine if there is an expression to evaluate
		const ctx = this.context;
		const evalMap = this._evalIds;
		const evalKey = node.$URL + ":" + idAttr;
		let expr: string;
		let exprKey: string;
		let rc: ReflectionClass;
		let v: any;

		if (!evalMap[evalKey]) {
			rc = reflection.getNodeClass(node);
			expr = rc && rc.getRollupExpression(idAttr);
			exprKey = (expr != null) && ([ctx?.view?.getViewPreferenceKey() || "", rc.getType(), idAttr].join("/"));

			// mark attribute so that it will not be evaluated again
			evalMap[evalKey] = true;
		}

		if (exprKey && !_failed[exprKey]) {
			// only evaluate if attribute has expression that is not disabled and node is not frozen
			const fg = xml.nodeAttributeGetFlags(node, idAttr) as number;

			if (xml.valueHasFlag(fg, xml.ATTR_FLAG_HAS_EXPRESSION) &&
				!xml.valueHasFlag(fg, xml.ATTR_FLAG_DISABLE_EXPRESSION) &&
				(
					!xml.nodeIsFrozen(node) ||
					xml.valueHasFlag(fg, xml.ATTR_FLAG_CALCULATE_WHEN_FROZEN) ||
					xml.valueHasFlag(fg, xml.ATTR_FLAG_ONE_TIME_FREEZE_ROLLUP)
				)
			) {
				try {
					// rule out expressions with try-catch blocks
					if (expr.indexOf("{try{") > 0) {
						throw new Error("Expressions with try-catch blocks cannot be evaluated on the client");
					}

					// evaluate expression and assign new value
					v = this.evaluate(expr, null, node, params);
					if (v != null) {
						if (_.isNumber(v) && ((v === Infinity) || isNaN(v))) {
							// client cannot catch divide-by-zero or divide-by-null cases as server does, so do not accept value if number not valid;
							// however, do not stop evaluating expression in such a case
							if (v != node[idAttr]) {
								this.toConsole(new Error("Numeric expression did not result in a valid number."), exprKey + " : " + expr);
							}
						} else {
							xml.nodeAttributeSetValue(node, idAttr, _.clone(v), true);
						}
					}
				} catch (ex) {
					// indicate that expression cannot be evaluated on client
					this.toConsole(ex, exprKey + " : " + expr);
					_failed[exprKey] = true;
				}
			}
		}

		// store attribute if it is a dependency of another expression - assumption is to disregard the path as
		// it should be rare that attributes with the same id but on different nodes will have an expression on one
		// and not the other; this information is only used in logic to refresh client after attribute change
		if (bDependency) {
			this._dependencyIds = this._dependencyIds || {};
			this._dependencyIds[idAttr] = true;
		}

		v = node[idAttr];
		if (null == v) {
			var prop = PSEUDO_ATTRIBUTES_MAP[idAttr];
			if (null != prop) {
				v = node[prop];
			}
		}

		// when evaluating expressions on forms, the node may not include all attributes from the class, so lookup default value instead
		if ((v == null) && xml.isClientUrl(node.$URL)) {
			var rca = reflection.getAttribute(node, idAttr);
			if (rca) {
				v = rca.getValue();
			}
		}

		return v;
	}
}

export = Processor;